"use client";
import React, { useMemo, useEffect, useState } from "react";
import { Form, Select, Input, message, DatePicker } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useOutletContext } from "react-router-dom";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import useMedicalData from "@/Profile/hooks/useMedicalData";
import { useUserInformation } from "@/Profile/hooks/useUserInformation";
import { fetchUserById } from "../../../../Profile/redux/thunks/userThunks";
import { selectEffectiveUserId } from "../../../../sous-profile/redux/selectors/sousProfileSelectors";
import { triggerProfileUpdate } from "@/utils/profileCompletion";
import "./GeneralForm.css";

const normalizeBlood = (v) =>
  v ? String(v).trim().toUpperCase().replace(/\s+/g, "") : "";

/* ✅ Calculate age from birthdate */
const calcAge = (raw) => {
  if (!raw) return null;
  try {
    const d = dayjs.isDayjs(raw) ? raw.toDate() : new Date(raw);
    if (Number.isNaN(d.getTime())) return null;

    const today = new Date();
    let age = today.getFullYear() - d.getFullYear();
    const m = today.getMonth() - d.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < d.getDate())) {
      age -= 1;
    }
    return age >= 0 ? age : null;
  } catch (e) {
    return null;
  }
};

export default function GeneralForm() {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const [calculatedAge, setCalculatedAge] = useState(null);
  const effectiveUserId = useSelector(selectEffectiveUserId);
  const user = useSelector((s) => effectiveUserId ? s.user.users[effectiveUserId] : null);

useEffect(() => {
    if (effectiveUserId && !user) dispatch(fetchUserById(effectiveUserId));
  }, [dispatch, effectiveUserId, user]);

  const userId = effectiveUserId || null;

  const {
    bloodType,
    diseases,
    height,
    weight,
    handleSaveClick,   // ✅ handles ALL medical data including blood type
    handleBmiSaveClick,
  } = useMedicalData(userId);

  // useUserInformation only handles user fields (gender, email, phone)
  const { handleSubmit } = useUserInformation(user, null);

  const { registerSave } = useOutletContext() || {};
  useEffect(() => {
    if (registerSave) registerSave(() => form.submit());
    return () => registerSave?.(null);
  }, [registerSave, form]);

  const initialValues = useMemo(() => ({
    gender:   user?.gender || "",
    email:    user?.email  || "",
    phone:    user?.phone  || "",
    blood:    normalizeBlood(bloodType),
    height:   height ? `${height} Cm` : "",
    weight:   weight ? `${weight} Kg` : "",
    diseases: diseases || "",
    birthdate: user?.birthdate ? dayjs(user.birthdate) : null,
  }), [user, bloodType, height, weight, diseases]);

  useEffect(() => {
    form.setFieldsValue(initialValues);
    // Calculate age on initial load
    if (initialValues.birthdate) {
      const age = calcAge(initialValues.birthdate);
      setCalculatedAge(age);
    }
  }, [initialValues, form]);

  /* OPTIONS */
  const genderOptions = useMemo(() => [
    { value: "female", label: t("generalForm.options.gender.female") },
    { value: "male",   label: t("generalForm.options.gender.male")   },
    { value: "other",  label: t("generalForm.options.gender.other")  },
  ], [t]);

  const bloodOptions = useMemo(() => {
    const base = ["O+", "O-", "A+", "A-", "B+", "B-", "AB+", "AB-"];
    const current = normalizeBlood(bloodType);
    const options = base.map((v) => ({ value: v, label: v }));
    if (current && !base.includes(current)) {
      return [{ value: current, label: current }, ...options];
    }
    return options;
  }, [bloodType]);
  const heightOptions = useMemo(() =>
    Array.from({ length: 231 }, (_, i) => 20 + i).map((n) => ({
      value: `${n} Cm`, label: `${n} Cm`,
    })), []);

  const weightOptions = useMemo(() =>
    Array.from({ length: 600 }, (_, i) => (i + 1) / 2).map((n) => ({
      value: `${n} Kg`, label: `${n} Kg`,
    })), []);

  /* SUBMIT */
  const handleFinish = async (values) => {
    const tailleCm = values.height ? parseFloat(values.height) : undefined;
    const poidsKg  = values.weight ? parseFloat(values.weight) : undefined;
    const birthdateStr = values.birthdate ? values.birthdate.format("YYYY-MM-DD") : undefined;

    try {
      // 1. Save user-only fields (gender, email, phone, birthdate)
      await handleSubmit({
        gender: values.gender,
        email:  values.email,
        phone:  values.phone,
        birthdate: birthdateStr,
      });

      // 2. Save ALL medical fields via handleSaveClick —
      //    blood type + diseases go as overrides, picked up directly in the payload
      await handleSaveClick({
        groupeSanguin:      normalizeBlood(values.blood),
        maladiesChroniques: values.diseases,
      });

      // 3. Save height/weight as bmi_history entry only if changed
      if (tailleCm && poidsKg) {
        const heightChanged = tailleCm !== height;
        const weightChanged = poidsKg  !== weight;
        if (heightChanged || weightChanged) {
          await handleBmiSaveClick(tailleCm, poidsKg);
        }
      }

      triggerProfileUpdate();
      message.success(t("generalForm.success") || "Profile updated successfully!");
    } catch (error) {
      console.error("Save error:", error);
      message.error(t("generalForm.error") || "Failed to update profile");
    }
  };

  /* ✅ Handle birthdate change to calculate age */
  const handleBirthdateChange = (date) => {
    if (date) {
      const age = calcAge(date);
      setCalculatedAge(age);
    } else {
      setCalculatedAge(null);
    }
  };

  return (
    <div className="gf-card">
      <section className="gf-section">
        <h4 className="gf-section-title">{t("generalForm.sections.general")}</h4>

        <Form
          form={form}
          layout="vertical"
          initialValues={initialValues}
          onFinish={handleFinish}
          className="gf-form"
        >
          <div className="gf-grid">
            <Form.Item label={t("generalForm.fields.gender")} name="gender">
              <Select options={genderOptions} disabled />
            </Form.Item>

            <Form.Item label={t("generalForm.fields.blood")} name="blood">
              <Select options={bloodOptions} allowClear={false} className="gf-select" />
            </Form.Item>

            <Form.Item label={t("generalForm.fields.height")} name="height">
              <Select showSearch optionFilterProp="label" options={heightOptions} className="gf-select" />
            </Form.Item>

            <Form.Item label={t("generalForm.fields.weight")} name="weight">
              <Select showSearch optionFilterProp="label" options={weightOptions} className="gf-select" />
            </Form.Item>

            <Form.Item label={t("generalForm.fields.dateOfBirth") || "Date of Birth"} name="birthdate">
              <DatePicker 
                onChange={handleBirthdateChange}
                format="YYYY-MM-DD"
                placeholder="Select your date of birth"
              />
            </Form.Item>

            {calculatedAge != null && (
              <div className="gf-age-display">
                <strong>Age: {calculatedAge} yrs</strong>
              </div>
            )}
          </div>

          <h4 className="gf-section-title">{t("generalForm.sections.contact")}</h4>
          <div className="gf-grid">
            <Form.Item label={t("generalForm.fields.email")} name="email">
              <Input disabled />
            </Form.Item>
            <Form.Item label={t("generalForm.fields.phone")} name="phone">
              <Input disabled />
            </Form.Item>
          </div>

          {/* <h4 className="gf-section-title">{t("generalForm.sections.medical")}</h4>
          <Form.Item label={t("generalForm.fields.diseases")} name="diseases">
            <Input.TextArea rows={3} />
          </Form.Item> */}
        </Form>
      </section>
    </div>
  );
}
